import XlinkAPI from '~/app/api/xlinkAPI.js';

/**
 * Locks a return, surfacing any Prior Year Fee notice the server attaches (CORP-1199).
 *
 * The server signals an outstanding acknowledgment by refusing the lock with a
 * 409 carrying the notice copy. That refusal is NOT an error for the preparer
 * it is the normal path into the notice, so it must be told apart from an
 * ordinary lock conflict (someone else already holds the return). Only the
 * former is handled here. Genuine failures are rethrown so callers keep their
 * existing behavior rather than gaining a new way to strand preparers outside
 * returns they are entitled to open.
 *
 * @param {number} returnID return to lock.
 * @returns {Promise<{locked: boolean, notice: Object|null}>} `locked` is false
 *   when the lock was refused pending acknowledgment. `notice` carries mode,
 *   noticeVersionID, noticeTitle, noticeBody and remindersEnabled when present.
 * @throws rethrows anything that is not an acknowledgment-required refusal.
 */
export const lockReturnWithPriorYearNotice = async returnID => {
  try {
    const res = await XlinkAPI.lockReturnByReturnID(returnID);
    return { locked: true, notice: res?.data?.priorYearNotice ?? null };
  } catch (error) {
    const notice =
      error?.response?.status === 409 ? error?.response?.data?.priorYearNotice : undefined;

    if (notice?.mode) {
      return { locked: false, notice };
    }

    throw error;
  }
};

/**
 * Records the acknowledgment, then hands the checkbox state to the caller.
 *
 * These are two distinct writes on purpose, and the order matters. The
 * acknowledgment is the audit record and is awaited. The reminder preference is
 * an ordinary login setting dispatched through the store afterwards, where the
 * existing saga owns its own error handling. Anything going wrong with the
 * preference therefore cannot cost us the record.
 *
 * @param {number} noticeVersionID version displayed to the preparer.
 * @param {boolean} suppressReminders whether "Do not remind me again" was checked.
 * @param {Function} [onPreferenceChosen] persists the reminder preference.
 * @returns {Promise<void>}
 */
export const acknowledgePriorYearNotice = async (
  noticeVersionID,
  suppressReminders,
  onPreferenceChosen,
) => {
  await XlinkAPI.acknowledgePriorYearNotice(noticeVersionID);

  if (typeof onPreferenceChosen === 'function') {
    onPreferenceChosen(suppressReminders);
  }
};
